import { zodResolver } from "@hookform/resolvers/zod";
import { CirclePlus, Eye, Trash2 } from "lucide-react";
import { observer, useLocalObservable } from "mobx-react-lite";
import { type FC, useEffect, useRef, useState } from "react";
import {
  useFieldArray,
  useForm,
  type UseFormReturn,
  useWatch,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  COLLECTION_FILTER_LABEL,
  COLLECTION_GROUP_BY_LABEL,
  COLLECTION_TYPE_LABEL,
  CollectionFilterIdEnum,
  CollectionGroupByEnum,
  CollectionType,
  type FilterCollectionType,
  type OptionItem,
  type OptionsResponse,
  OptionType,
  useGetOptionsQuery,
} from "@/api/admin";
import { invalidateEntityHistory } from "@/api/admin/auditLog";
import {
  COLLECTION_MEDIA_ACCEPTED_TYPES,
  type CollectionCreateRequest,
  CollectionMediaValidation,
  type CollectionPreviewRequest,
  type CollectionPreviewResponse,
  type Filter,
  type ICollection,
  useDeactivateCollectionMutation,
  usePreviewCollectionMutation,
  useUpdateCollectionMutation,
} from "@/api/admin/collections";
import { getBranchAttributeLabel } from "@/api/admin/properties";
import { commonApi } from "@/api/common";
import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import {
  BlockWithBorder,
  CustomButton,
  CustomCardBody,
  CustomCardFooter,
  ErrorForm,
  FormCardBasic,
  HeaderWithBack,
  type ItemsListType,
  LabelWithOptional,
  Loader,
  SimpleText,
  Subtitle,
} from "@/components/atoms";
import {
  AdminPageActions,
  InputControl,
  MultiSelectWithTags,
  SelectControl,
  TextareaControl,
  toastError,
} from "@/components/molecules";
import { Form } from "@/components/ui/form";
import { PATHS } from "@/config/path";
import { commonIconProps20 } from "@/utils/constants";
import { FIELDS_MAPPING } from "@/utils/constants/allFieldsMapping";
import { handleErrorToast } from "@/utils/handleErrorToast";

import { EntityHistory } from "../EntityHistory";
import { useCollectionHistoryResolvers } from "../EntityHistory/hooks/useCollectionHistoryResolvers";
import { MediaStore, MediaUploader, UploadedFiles } from "../MediaUploader";
import { CollectionPreview } from "./CollectionPreview";
import {
  type FilterItem,
  NewCategoryFormSchema,
  type NewCategoryFormType,
} from "./types";
import {
  isCollectionLocationFilter,
  useCollectionLocationFilters,
} from "./useCollectionLocationFilters";

function normalizeValues(
  values: number[] | string[] | OptionItem[]
): OptionItem[] {
  return values.map(v =>
    typeof v === "number" || typeof v === "string"
      ? { id: String(v), name: String(v) }
      : v
  );
}

function getFilterValuesById(
  filterId: string | undefined,
  collectionFilters: FilterCollectionType[],
  getLocationOptions: (filterId: string | undefined) => OptionItem[] | null
): OptionItem[] {
  if (!filterId) return [];

  const locationOptions = getLocationOptions(filterId);
  if (locationOptions !== null) return locationOptions;

  const filter = collectionFilters.find(f => f.id === filterId);
  return filter ? normalizeValues(filter.values) : [];
}

const LOCATION_FILTER_EMPTY_TEXT: Partial<Record<string, string>> = {
  [CollectionFilterIdEnum.AREAS]: "No areas found.",
  [CollectionFilterIdEnum.STREETS]: "No streets found.",
  [CollectionFilterIdEnum.LANDMARKS]: "No landmarks found.",
};

const COLLECTION_PREVIEW_LOCATION = {
  latitude: 24.71138,
  longitude: 46.674353,
} as const;

const COLLECTION_PREVIEW_LIMIT = 20;

export function isCollectionFilterId(
  value: string
): value is CollectionFilterIdEnum {
  return Object.values(CollectionFilterIdEnum).includes(
    value as CollectionFilterIdEnum
  );
}

interface SystemCategoryFormProps {
  defaultValues: NewCategoryFormType;
  submit: (data: CollectionCreateRequest) => void;
  isLoading?: boolean;
}

export const SystemAddNewCategory: FC<SystemCategoryFormProps> = observer(
  ({ defaultValues, submit, isLoading }) => {
    const navigate = useNavigate();
    const { id = "" } = useParams();

    const { mutateAsync: mutateDeactivate } = useDeactivateCollectionMutation();
    const { mutateAsync: updateCollection } = useUpdateCollectionMutation();

    const mediaStore = useLocalObservable(
      () => new MediaStore(undefined, CollectionMediaValidation)
    );
    const [errorMedia, setErrorMedia] = useState<string | null>(null);
    const [previewData, setPreviewData] =
      useState<CollectionPreviewResponse | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isPreparingPreview, setIsPreparingPreview] = useState(false);
    const uploadedMediaRef = useRef<{ file: File; id: string } | null>(null);
    const previewRequestRef = useRef<CollectionPreviewRequest | null>(null);
    const collectionHistoryResolvers = useCollectionHistoryResolvers();
    const { mutateAsync: previewCollection, isPending: isPreviewPending } =
      usePreviewCollectionMutation();

    const { data: optionsData, isPending: isOptionPending } =
      useGetOptionsQuery([
        OptionType.Brands,
        OptionType.CollectionTypes,
        OptionType.CollectionFilters,
        OptionType.CollectionGroupBy,
      ]);

    const REELS_TYPE_OPTION = {
      id: CollectionType.REELS,
      name: COLLECTION_TYPE_LABEL[CollectionType.REELS],
      disabled: true,
    };

    const collectionTypeOptions: ItemsListType = [
      ...(optionsData?.collectionTypes?.map(type => ({
        id: type,
        name: COLLECTION_TYPE_LABEL[type],
      })) ?? []),
      REELS_TYPE_OPTION,
    ];

    const collectionGroupByOptions: ItemsListType =
      optionsData?.collectionGroupBy?.map(item => ({
        id: item,
        name: COLLECTION_GROUP_BY_LABEL[item as CollectionGroupByEnum] ?? item,
      })) ?? [];

    const filterIdOptions: ItemsListType =
      optionsData?.collectionFilters?.map(f => ({
        id: f.id,
        name: isCollectionFilterId(f.id)
          ? COLLECTION_FILTER_LABEL[f.id]
          : (f.group ?? f.type ?? f.id),
      })) ?? [];

    const form = useForm<NewCategoryFormType>({
      resolver: zodResolver(NewCategoryFormSchema),
      defaultValues,
      mode: "onChange",
      reValidateMode: "onBlur",
    });

    const {
      control,
      handleSubmit,
      formState: { isValid },
    } = form;

    const { fields, append, remove } = useFieldArray({
      control,
      name: "filters",
    });

    const watchedFilters = useWatch({ control, name: "filters" });
    const selectedFilters: FilterItem[] = watchedFilters ?? [];
    const {
      getLocationOptions,
      isLocationFilterDisabled,
      locationPlaceholder,
    } = useCollectionLocationFilters({ form, filters: selectedFilters });

    const isAddDisabled =
      selectedFilters.length === filterIdOptions.length &&
      filterIdOptions.length > 0;
    const selectedType = form.watch("type");
    const isSelectableGroup = selectedType === "SELECTABLE_GROUP";

    useEffect(() => {
      if (!isSelectableGroup) {
        form.setValue("groupBy", undefined);
      }
    }, [selectedType, isSelectableGroup, form]);

    // Restore media from mediaItem when editing
    useEffect(() => {
      if (defaultValues?.mediaItem) {
        const mediaItem = defaultValues.mediaItem;
        const mediaFile = {
          id: mediaItem.url,
          previewUrl: mediaItem.url,
          type: mediaItem.type,
        };

        mediaStore.restoreFromPayload([mediaFile]);
      } else {
        mediaStore.resetStore();
      }
    }, [defaultValues?.mediaItem, defaultValues?.mediaItemId, mediaStore]);

    const backNavigate = () => {
      navigate(`${PATHS.ADMIN.SYSTEMS_APP_CONTROL}#content:collections`);
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const resolveUploadedMediaItemId = async () => {
      const mediaFile = mediaStore.mediaFiles[0];
      if (!mediaFile?.file) return undefined;

      if (uploadedMediaRef.current?.file === mediaFile.file) {
        return uploadedMediaRef.current.id;
      }

      const response = await commonApi.mediaUpload(mediaFile.file);
      uploadedMediaRef.current = { file: mediaFile.file, id: response.id };
      return response.id;
    };

    const resolvePreviewMediaItemId = async () => {
      const uploadedMediaItemId = await resolveUploadedMediaItemId();
      if (uploadedMediaItemId) return uploadedMediaItemId;

      if (mediaStore.mediaFiles.length === 0) return undefined;

      return defaultValues.mediaItemId ?? undefined;
    };

    const normalizeCollectionData = (
      data: NewCategoryFormType,
      mediaItemId?: string | null
    ): CollectionCreateRequest => {
      const normalizedFilters: Filter[] =
        data.filters
          ?.filter(f => f.id && f.value && f.value.length > 0)
          .flatMap(f => {
            const originalFilter = optionsData?.collectionFilters?.find(
              cf => cf.id === f.id
            );
            if (!originalFilter) return [];

            const availableValues = getFilterValuesById(
              f.id,
              optionsData?.collectionFilters ?? [],
              getLocationOptions
            );

            return (f.value ?? [])
              .map(val => {
                const matchedValue = availableValues.find(v => v.id === val);
                const isLocationValue = isCollectionLocationFilter(
                  originalFilter.id
                );

                if (!matchedValue && !isLocationValue) return null;

                return {
                  id: originalFilter.id,
                  type: originalFilter.type,
                  value: matchedValue?.id ?? val,
                  ...(originalFilter.group && {
                    group: originalFilter.group,
                  }),
                } as Filter;
              })
              .filter((filter): filter is Filter => filter !== null);
          }) ?? [];

      return {
        titleEn: data.titleEn,
        titleAr: data.titleAr,
        subtitleEn: data.subtitleEn,
        subtitleAr: data.subtitleAr,
        type: data.type as CollectionPreviewRequest["type"],
        brands: data.brands,
        groupBy: data.groupBy ?? null,
        filters: normalizedFilters.length > 0 ? normalizedFilters : undefined,
        ...(mediaItemId ? { mediaItemId } : {}),
      };
    };

    const onPreview = async (data: NewCategoryFormType) => {
      setErrorMedia(null);
      setIsPreparingPreview(true);
      try {
        const mediaItemId = await resolvePreviewMediaItemId();
        const request = {
          ...normalizeCollectionData(data, mediaItemId),
          ...COLLECTION_PREVIEW_LOCATION,
          limit: COLLECTION_PREVIEW_LIMIT,
          offset: 0,
        };
        const result = await previewCollection(request);
        previewRequestRef.current = request;
        setPreviewData(result);
        setIsPreviewOpen(true);
      } catch {
        toastError("Unable to generate collection preview.", () => {}, "Error");
      } finally {
        setIsPreparingPreview(false);
      }
    };

    const onPreviewOffsetChange = async (offset: number) => {
      if (!previewRequestRef.current) return;

      try {
        const request = { ...previewRequestRef.current, offset };
        const result = await previewCollection(request);
        previewRequestRef.current = request;
        setPreviewData(result);
      } catch {
        toastError("Unable to load matching branches.", () => {}, "Error");
      }
    };

    const onSubmit = async (data: NewCategoryFormType) => {
      setIsSubmitting(true);
      try {
        let finalMediaItemId: string | undefined | null;

        // Handle media upload
        if (mediaStore.mediaFiles.length > 0) {
          finalMediaItemId = await resolveUploadedMediaItemId();
        } else {
          finalMediaItemId = null;
        }

        submit({
          ...normalizeCollectionData(data, finalMediaItemId),
          mediaItemId: finalMediaItemId,
        });

        mediaStore.resetStore();
      } catch {
        setErrorMedia("Error in uploading media");
      } finally {
        setIsSubmitting(false);
      }
    };

    const toggleDeactivate = async () => {
      try {
        if (defaultValues?.active) {
          await mutateDeactivate(defaultValues.id || "");
        } else {
          // Get current collection data to preserve other fields
          const currentData = queryClient.getQueryData<ICollection>([
            QUERY_KEYS.collections,
            id,
          ]);

          if (currentData) {
            await updateCollection({
              id: defaultValues.id || "",
              data: {
                active: true,
              },
            });
          }
        }

        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.collections, id],
        });
        await invalidateEntityHistory({ scope: id });

        return {
          isSuccess: true,
          isError: false,
        };
      } catch (error) {
        handleErrorToast(error, () => toggleDeactivate());
        return {
          isSuccess: false,
          isError: true,
        };
      }
    };

    const selectedFilterIds = selectedFilters.map(f => f.id).filter(Boolean);

    const FilterRow = ({
      form,
      index,
      optionsData,
      getLocationOptions,
      isLocationFilterDisabled,
      locationPlaceholder,
    }: {
      form: UseFormReturn<NewCategoryFormType>;
      index: number;
      optionsData: OptionsResponse | undefined;
      getLocationOptions: (filterId: string | undefined) => OptionItem[] | null;
      isLocationFilterDisabled: (filterId: string | undefined) => boolean;
      locationPlaceholder: string;
    }) => {
      const { control } = form;

      const selectedFilterId = useWatch({
        control: control,
        name: `filters.${index}.id`,
      });
      const filterValueOptions = getFilterValuesById(
        selectedFilterId,
        optionsData?.collectionFilters ?? [],
        getLocationOptions
      );
      const isLocationDependent = isCollectionLocationFilter(selectedFilterId);

      const availableFilterOptions = filterIdOptions.filter(
        option =>
          option.id === selectedFilterId ||
          !selectedFilterIds.includes(option.id)
      );

      return (
        <div className="flex items-start mt-5">
          <div className="grid grid-cols-2 gap-1 sm:gap-5 w-full">
            <SelectControl<NewCategoryFormType>
              form={form}
              nameSelect={`filters.${index}.id`}
              classes="flex-1"
              itemsList={availableFilterOptions}
              placeholder="Select filter"
              triggerProps={{
                "data-testid": `filters-id-select-${index}`,
              }}
              onValueChange={() =>
                form.setValue(`filters.${index}.value`, [], {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
            <div className="flex w-full">
              <MultiSelectWithTags
                form={form}
                nameSelect={`filters.${index}.value`}
                tagsList={filterValueOptions}
                disabled={
                  !selectedFilterId ||
                  isLocationFilterDisabled(selectedFilterId)
                }
                placeholder={
                  isLocationDependent ? locationPlaceholder : "Select multiple"
                }
                emptyText={LOCATION_FILTER_EMPTY_TEXT[selectedFilterId ?? ""]}
                grouped={
                  selectedFilterId === CollectionFilterIdEnum.BRANCH_TAGS
                }
                getGroupLabel={getBranchAttributeLabel}
                data-testid={`filters-value-select-${index}`}
                classes="w-full"
              />
              <CustomButton
                variant="icon"
                className="p-0! ml-2 sm:ml-[25px] mr-[6px] mb-auto self-end"
                onClick={() => remove(index)}
                data-testid={`filter-remove-${index}`}
              >
                <Trash2 {...commonIconProps20} />
              </CustomButton>
            </div>
          </div>
        </div>
      );
    };

    if (isOptionPending) {
      return <Loader fullscreen />;
    }

    return (
      <FormCardBasic>
        <HeaderWithBack
          linkProps={{
            to: `${PATHS.ADMIN.SYSTEMS_APP_CONTROL}#content:collections`,
            onClick: () => backNavigate(),
          }}
          title={defaultValues.titleEn || "New collection"}
        >
          <div className="flex items-center gap-5">
            {id && (
              <EntityHistory
                entityId={id}
                entityName={defaultValues.titleEn}
                blockName="Collection details"
                mapKeyToName={FIELDS_MAPPING.collectionDetail}
                fieldResolvers={collectionHistoryResolvers}
              />
            )}
            <AdminPageActions
              state="collection"
              name={defaultValues.titleEn || ""}
              disabled={defaultValues.id ? false : true}
              onConfirm={toggleDeactivate}
              active={defaultValues?.active}
            />
          </div>
        </HeaderWithBack>

        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="system-form-new-category"
          >
            <CustomCardBody classes="pb-7 px-4 md:px-8">
              <div className="flex flex-col">
                <BlockWithBorder classes="mb-8.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-4">
                    <InputControl<NewCategoryFormType>
                      formControl={control}
                      label="Collection name (English)"
                      nameField="titleEn"
                      typeField="text"
                      data-testid="system-category-name-english-input"
                    />
                    <InputControl<NewCategoryFormType>
                      formControl={control}
                      label="Collection name (Arabic)"
                      nameField="titleAr"
                      typeField="text"
                      data-testid="system-category-name-arabic-input"
                      dir="rtl"
                    />
                  </div>
                </BlockWithBorder>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-5">
                  <TextareaControl<NewCategoryFormType>
                    formControl={control}
                    label="Description (English)"
                    nameField="subtitleEn"
                    data-testid="system-category-description-english"
                  />
                  <TextareaControl<NewCategoryFormType>
                    label="Description (Arabic)"
                    formControl={control}
                    nameField="subtitleAr"
                    data-testid="system-category-description-arabic"
                    dir="rtl"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-4.5">
                  <div className="flex flex-col gap-1">
                    <LabelWithOptional label="Brands" classes="text-sm" />
                    <MultiSelectWithTags
                      form={form}
                      nameSelect="brands"
                      tagsList={optionsData?.brands || []}
                    />
                  </div>
                  <SelectControl<NewCategoryFormType>
                    form={form}
                    nameSelect="type"
                    label="Type"
                    placeholder="Select"
                    classes="flex-1 mb-auto"
                    itemsList={collectionTypeOptions}
                    triggerProps={{ "data-testid": "type-select-app-control" }}
                  />
                </div>

                <div className="mb-4">
                  <div className="mb-[14px] text-sm">
                    <LabelWithOptional label="Filters" />
                    <div className="flex flex-col">
                      {fields.map((elem, index) => (
                        <FilterRow
                          key={elem.id}
                          form={form}
                          index={index}
                          optionsData={optionsData}
                          getLocationOptions={getLocationOptions}
                          isLocationFilterDisabled={isLocationFilterDisabled}
                          locationPlaceholder={locationPlaceholder}
                        />
                      ))}
                    </div>
                  </div>

                  <CustomButton
                    size="sm"
                    type="button"
                    variant="link"
                    className="relative left-[-14px]"
                    onClick={() => append({ id: "", value: [] })}
                    disabled={isAddDisabled}
                    data-testid="system-add-filter-button"
                  >
                    <CirclePlus {...commonIconProps20} color="#155FCC" />
                    Add filter
                  </CustomButton>
                </div>

                {isSelectableGroup && (
                  <SelectControl<NewCategoryFormType>
                    form={form}
                    nameSelect="groupBy"
                    label="Group by"
                    placeholder="Select"
                    classes="w-full sm:w-[49%]"
                    itemsList={collectionGroupByOptions}
                    triggerProps={{
                      "data-testid": "groupBy-select-app-control",
                    }}
                  />
                )}

                <div className="mb-4 mt-4">
                  <Subtitle classes="mb-1" title="Media" />
                  <SimpleText classes="mb-6">
                    <span className="font-bold">Photo requirements:</span> use a
                    JPG, PNG or WEBP. Keep it under 3 MB for faster loading.
                    Dimensions: Image (700X700px).
                    <span className="font-bold">Video requirement:</span> For
                    best results, uploads should be at least 1080p (1080 x 1080
                    pixels) in MP4 format. Keep it under 100 MB
                  </SimpleText>
                  <div className="flex gap-2">
                    {mediaStore.mediaFiles.map((media, index) => (
                      <UploadedFiles
                        media={media}
                        key={index}
                        index={index}
                        mediaStore={mediaStore}
                      />
                    ))}
                    <MediaUploader
                      acceptedTypes={COLLECTION_MEDIA_ACCEPTED_TYPES}
                      disabled={
                        mediaStore.isImageLimitReached ||
                        mediaStore.isVideoLimitReached
                      }
                      mediaStore={mediaStore}
                      onError={setErrorMedia}
                    />
                  </div>
                  {errorMedia && <ErrorForm message={errorMedia} />}
                </div>
              </div>
            </CustomCardBody>

            <CustomCardFooter>
              <CustomButton
                type="button"
                variant="light"
                data-testid="system-cancel-button"
                onClick={backNavigate}
              >
                Cancel
              </CustomButton>
              <div className="flex items-center gap-3">
                <CustomButton
                  type="button"
                  variant="light"
                  data-testid="system-preview-button"
                  disabled={!isValid || isSubmitting || isPreparingPreview}
                  isPending={isPreparingPreview || isPreviewPending}
                  onClick={handleSubmit(onPreview)}
                >
                  <Eye className="size-4" />
                  Preview
                </CustomButton>
                <CustomButton
                  type="submit"
                  data-testid="system-submit-button"
                  disabled={!isValid || isSubmitting || isPreparingPreview}
                  isPending={isLoading || isSubmitting}
                >
                  Save
                </CustomButton>
              </div>
            </CustomCardFooter>
          </form>
        </Form>

        <CollectionPreview
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          data={previewData}
          isPagePending={isPreviewPending}
          onOffsetChange={onPreviewOffsetChange}
        />
      </FormCardBasic>
    );
  }
);

export default SystemAddNewCategory;
